import { useRef, useState, type FormEvent } from 'react';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle,
  ChevronRight,
  Fingerprint,
  Lightbulb,
  Loader2,
  Mail,
  Sparkles,
  Type,
  User,
  Zap,
} from 'lucide-react';
import PublicContactSuccessCard from './PublicContactSuccessCard';
import {
  getContactSubmitErrorMessage,
  isValidEmail,
  normalizeText,
  postContactSubmission,
  type ContactFieldErrors,
  type InquiryType,
} from './contactFormShared';
import type { PublicRoutePath } from './publicRoutes';
import useContactTurnstile from './useContactTurnstile';

type FeatureRequestPageProps = {
  onNavigate?: (path: PublicRoutePath, options?: { hash?: string | null }) => void;
};

type SuggestionType =
  | 'Feature request'
  | 'Workflow improvement'
  | 'Broker integration'
  | 'Chart / replay improvement'
  | 'Analytics idea'
  | 'Other';

type FeatureRequestFormState = {
  type: SuggestionType;
  name: string;
  email: string;
  title: string;
  description: string;
  companyWebsite: string;
};

type FeatureRequestFieldErrors = Partial<Record<'name' | 'email' | 'title' | 'description', string>>;

const suggestionTypes: SuggestionType[] = [
  'Feature request',
  'Workflow improvement',
  'Broker integration',
  'Chart / replay improvement',
  'Analytics idea',
  'Other',
];

const INITIAL_FORM: FeatureRequestFormState = {
  type: 'Feature request',
  name: '',
  email: '',
  title: '',
  description: '',
  companyWebsite: '',
};

function validateFeatureRequestForm(form: FeatureRequestFormState): FeatureRequestFieldErrors {
  const nextErrors: FeatureRequestFieldErrors = {};

  if (!normalizeText(form.name)) nextErrors.name = 'Name is required.';
  if (!normalizeText(form.email)) {
    nextErrors.email = 'Email is required.';
  } else if (!isValidEmail(form.email)) {
    nextErrors.email = 'Enter a valid email address.';
  }
  if (!normalizeText(form.title)) nextErrors.title = 'Subject is required.';
  if (!normalizeText(form.description)) nextErrors.description = 'Message is required.';

  return nextErrors;
}

function mapContactFieldErrorsToFeature(fieldErrors?: ContactFieldErrors): FeatureRequestFieldErrors {
  if (!fieldErrors) return {};

  return {
    name: fieldErrors.name,
    email: fieldErrors.email,
    title: fieldErrors.subject,
    description: fieldErrors.message,
  };
}

function buildFeatureRequestMessage(type: SuggestionType, description: string) {
  return [`Suggestion type: ${type}`, '', normalizeText(description)].join('\n');
}

export default function FeatureRequestPage({ onNavigate }: FeatureRequestPageProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<FeatureRequestFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FeatureRequestFieldErrors>({});
  const [confirmationInquiryType, setConfirmationInquiryType] = useState<InquiryType | null>(null);
  const [confirmationTeamLabel, setConfirmationTeamLabel] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [deliveryNotice, setDeliveryNotice] = useState('');
  const isSubmittingRef = useRef(false);
  const {
    resetTurnstileWidget,
    turnstileError,
    turnstileHostRef,
    turnstileSiteKey,
    turnstileStatus,
    turnstileToken,
  } = useContactTurnstile();

  const isSubmitting = status === 'submitting';

  function updateField<K extends keyof FeatureRequestFormState>(field: K, value: FeatureRequestFormState[K]) {
    setFormData((previous) => ({ ...previous, [field]: value }));

    if (field === 'name' || field === 'email' || field === 'title' || field === 'description') {
      setErrors((previous) => ({ ...previous, [field]: '' }));
    }

    setSubmitError('');
  }

  function handleReset() {
    setStatus('idle');
    setSubmitError('');
    setFormData({ ...INITIAL_FORM });
    setErrors({});
    setConfirmationInquiryType(null);
    setConfirmationTeamLabel('');
    setConfirmationEmail('');
    setReferenceId('');
    setDeliveryNotice('');
    resetTurnstileWidget();
  }

  function isFieldValid(field: keyof Pick<FeatureRequestFormState, 'name' | 'email' | 'title'>) {
    if (field === 'email') return isValidEmail(formData.email);
    return Boolean(normalizeText(formData[field]));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const nextErrors = validateFeatureRequestForm(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmitError('');
      isSubmittingRef.current = false;
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitError('Please complete the security check before sending.');
      isSubmittingRef.current = false;
      return;
    }

    setStatus('submitting');
    setSubmitError('');

    try {
      const { response, body } = await postContactSubmission({
        name: formData.name,
        email: formData.email,
        inquiryType: 'Feature Request',
        subject: formData.title,
        message: buildFeatureRequestMessage(formData.type, formData.description),
        companyWebsite: formData.companyWebsite,
        turnstileToken: turnstileSiteKey ? turnstileToken : '',
      });

      if (!response.ok || !body?.ok) {
        resetTurnstileWidget();

        if (body?.fieldErrors) {
          setErrors(mapContactFieldErrorsToFeature(body.fieldErrors));
        }

        setSubmitError(getContactSubmitErrorMessage(body?.error));
        setStatus('idle');
        return;
      }

      setConfirmationInquiryType(body.inquiryType || 'Feature Request');
      setConfirmationTeamLabel(body.destinationTeamLabel || body.routedToLabel || 'Product');
      setConfirmationEmail(body.submitterEmail || formData.email.trim());
      setReferenceId(body.referenceId || '');
      setDeliveryNotice(
        body.deliveryWarning === 'confirmation_email_failed'
          ? 'Your message was sent, but the confirmation email could not be delivered. Our team still received your request.'
          : ''
      );
      setStatus('success');
      resetTurnstileWidget();
    } catch {
      resetTurnstileWidget();
      setSubmitError('We could not send your message right now. Please try again in a moment.');
      setStatus('idle');
    } finally {
      isSubmittingRef.current = false;
    }
  }

  if (status === 'success' && confirmationInquiryType) {
    return (
      <div className="min-h-screen bg-zinc-50 p-2 pt-28 font-sans text-zinc-900 sm:p-4 md:p-6 md:pt-32">
        <div className="mx-auto max-w-5xl px-8">
          <PublicContactSuccessCard
            inquiryType={confirmationInquiryType}
            confirmationTeamLabel={confirmationTeamLabel}
            confirmationEmail={confirmationEmail}
            referenceId={referenceId}
            deliveryNotice={deliveryNotice}
            onReset={handleReset}
            onReturnHome={() => onNavigate?.('/')}
            primaryActionLabel="Send another feature request"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 p-2 pt-28 font-sans text-zinc-900 sm:p-4 md:p-6 md:pt-32">
      <div className="mx-auto max-w-7xl px-8">
        <div className="overflow-hidden rounded-[3rem] border border-zinc-200 bg-white shadow-sm">
          <div className="w-full px-6 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10">
            <div className="relative mb-12 flex items-center justify-center">
              <button
                type="button"
                onClick={() => onNavigate?.('/')}
                className="absolute left-0 flex items-center gap-2.5 rounded-full border border-zinc-200 px-6 py-2.5 text-zinc-600 shadow-sm shadow-zinc-100/50 transition-all hover:bg-zinc-100 active:scale-95"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-[15px] font-medium tracking-tight">Back to main page</span>
              </button>
              <span className="text-[12px] font-black uppercase tracking-[0.45em] text-pink-500">FEATURE REQUEST FORM</span>
            </div>

            <header className="mb-8 flex flex-col justify-between gap-6 border-b border-zinc-200 pb-8 md:flex-row md:items-start">
              <div className="flex items-center gap-6">
                <div className="hidden h-20 w-20 shrink-0 items-center justify-center rounded-full bg-pink-500 shadow-xl shadow-pink-100 sm:flex">
                  <Lightbulb className="h-10 w-10 text-white" />
                </div>
                <div>
                  <h1 className="mb-3 max-w-5xl text-5xl font-bold leading-[1.3] tracking-tight text-black">Suggest a Feature</h1>
                  <p className="mt-2 max-w-xl text-lg leading-relaxed text-zinc-500">
                    Help us build exactly what you need. All suggestions are routed through the live support system and reviewed by our team.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 self-start rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200 bg-white shadow-sm">
                  <Fingerprint className="h-5 w-5 text-pink-500" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Product routing</p>
                  <p className="text-xs font-bold text-zinc-600">Feature Request</p>
                </div>
              </div>
            </header>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
              <div className="min-w-0">
                <form onSubmit={handleSubmit} className="flex h-full flex-col" noValidate aria-busy={isSubmitting}>
                  <div className="space-y-10">
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="feature-request-company-website">Company website</label>
                      <input
                        id="feature-request-company-website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.companyWebsite}
                        onChange={(event) => updateField('companyWebsite', event.target.value)}
                      />
                    </div>

                    <section className="space-y-4">
                      <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-zinc-400">
                        1. Suggestion type
                      </label>
                      <div className="grid grid-cols-1 gap-x-3 gap-y-6 sm:grid-cols-2">
                        {suggestionTypes.map((type) => (
                          <button
                            key={type}
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => updateField('type', type)}
                            className={`group relative overflow-hidden rounded-2xl border-2 px-6 py-3 text-left font-bold outline-none transition-all duration-300 ${
                              formData.type === type
                                ? 'scale-[1.02] border-pink-500 bg-pink-500 text-white shadow-lg shadow-pink-200'
                                : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-pink-200 hover:bg-white'
                            } ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''}`}
                          >
                            <div className="relative z-10 flex items-center justify-between">
                              <span>{type}</span>
                              {formData.type === type ? <Check className="h-4 w-4" /> : null}
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>
                    <div className="my-4 border-t border-zinc-200" />

                    <section className="space-y-4">
                      <label className="text-xs font-black uppercase tracking-widest text-zinc-400">2. Your details</label>
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div className="relative group">
                          <div className="relative">
                            <div className="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 items-center text-zinc-400 transition-colors duration-300 group-focus-within:text-pink-500">
                              <User className="h-4 w-4" />
                            </div>
                            <input
                              type="text"
                              disabled={isSubmitting}
                              placeholder="Your full name"
                              className={`w-full rounded-2xl border-2 py-3.5 pl-11 pr-11 font-regular outline-none transition-all ${
                                errors.name ? 'border-pink-500 bg-pink-50' : 'border-zinc-200 bg-zinc-100/50 focus:border-pink-500 focus:bg-white'
                              }`}
                              value={formData.name}
                              onChange={(event) => updateField('name', event.target.value)}
                            />
                          </div>
                          {isFieldValid('name') && !errors.name ? (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-pink-500">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                          ) : null}
                          {errors.name ? (
                            <p className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-pink-500">
                              <AlertCircle className="h-3.5 w-3.5" />
                              {errors.name}
                            </p>
                          ) : null}
                        </div>

                        <div className="relative group">
                          <div className="relative">
                            <div className="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 items-center text-zinc-400 transition-colors duration-300 group-focus-within:text-pink-500">
                              <Mail className="h-4 w-4" />
                            </div>
                            <input
                              type="email"
                              disabled={isSubmitting}
                              placeholder="Email address"
                              className={`w-full rounded-2xl border-2 py-3.5 pl-11 pr-11 font-regular outline-none transition-all ${
                                errors.email ? 'border-pink-500 bg-pink-50' : 'border-zinc-200 bg-zinc-100/50 focus:border-pink-500 focus:bg-white'
                              }`}
                              value={formData.email}
                              onChange={(event) => updateField('email', event.target.value)}
                            />
                          </div>
                          {isFieldValid('email') && !errors.email ? (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-pink-500">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                          ) : null}
                          {errors.email ? (
                            <p className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-pink-500">
                              <AlertCircle className="h-3.5 w-3.5" />
                              {errors.email}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </section>
                    <div className="my-4 border-t border-zinc-200" />

                    <section className="space-y-6">
                      <div className="space-y-4">
                        <label className="text-xs font-black uppercase tracking-widest text-zinc-400">3. The idea</label>
                        <div className="relative group">
                          <div className="absolute left-4 inset-y-0 flex items-center text-zinc-400 transition-colors duration-300 group-focus-within:text-pink-500">
                            <Type className="h-4 w-4" />
                          </div>
                          <input
                            type="text"
                            disabled={isSubmitting}
                            placeholder="Short title for your suggestion"
                            className={`w-full rounded-2xl border-2 py-3.5 pl-11 pr-11 font-regular outline-none transition-all ${
                              errors.title ? 'border-pink-500 bg-pink-50' : 'border-zinc-200 bg-zinc-100/50 focus:border-pink-500 focus:bg-white'
                            }`}
                            value={formData.title}
                            onChange={(event) => updateField('title', event.target.value)}
                          />
                          {isFieldValid('title') && !errors.title ? (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-pink-500">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                          ) : null}
                        </div>
                        {errors.title ? (
                          <p className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-pink-500">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {errors.title}
                          </p>
                        ) : null}
                      </div>

                      <div className="space-y-3">
                        <textarea
                          disabled={isSubmitting}
                          placeholder="Describe the friction you're experiencing or the solution you have in mind..."
                          className={`min-h-[325px] w-full resize-none rounded-3xl border-2 p-5 font-regular leading-relaxed outline-none transition-all ${
                            errors.description ? 'border-pink-500 bg-pink-50' : 'border-zinc-200 bg-zinc-100/50 focus:border-pink-500 focus:bg-white'
                          }`}
                          value={formData.description}
                          onChange={(event) => updateField('description', event.target.value)}
                        />

                        <div className="flex items-center justify-between gap-4 px-1">
                          {errors.description ? (
                            <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-pink-500">
                              <AlertCircle className="h-3.5 w-3.5" />
                              {errors.description}
                            </p>
                          ) : (
                            <div />
                          )}

                          <div className="flex items-center gap-3">
                            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-zinc-100">
                              <div
                                className={`h-full transition-all duration-500 ${formData.description.length >= 50 ? 'bg-[#c026d3]' : 'bg-pink-500'}`}
                                style={{ width: `${Math.min(100, (formData.description.length / 50) * 100)}%` }}
                              />
                            </div>
                            <span className={`text-[10px] font-black tracking-widest tabular-nums ${formData.description.length < 50 ? 'text-zinc-400' : 'text-[#c026d3]'}`}>
                              {formData.description.length < 50 ? `${formData.description.length}/50 MIN` : 'READY'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>

                    {turnstileSiteKey ? (
                      <div className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Security check</p>
                        <div className="rounded-[1.5rem] border border-pink-100 bg-white p-4 shadow-sm">
                          <div ref={turnstileHostRef} />
                          {turnstileStatus === 'loading' ? (
                            <p className="mt-3 text-xs font-normal leading-6 text-zinc-500">Loading the security check...</p>
                          ) : null}
                          {turnstileStatus === 'error' ? (
                            <p className="mt-3 text-xs font-normal leading-6 text-pink-500">
                              {turnstileError || 'The security check could not load. Refresh the page and try again.'}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {submitError ? (
                      <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-pink-50 p-4 text-sm font-medium text-red-600">
                        <AlertCircle className="h-5 w-5" />
                        {submitError}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-8 flex flex-col items-center justify-end gap-6 pt-8 sm:flex-row">
                    <button
                      type="submit"
                      disabled={isSubmitting || (turnstileSiteKey ? !turnstileToken : false)}
                      className="group flex w-full items-center justify-center gap-3 rounded-full bg-pink-500 px-12 py-4 text-[11px] font-bold uppercase tracking-[0.3em] text-white shadow-xl transition-all active:scale-95 hover:-translate-y-0.5 hover:bg-[#c026d3] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          SUGGEST A FEATURE <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              <aside className="flex min-h-full min-w-0 flex-col gap-6">
                <div className="rounded-[2.5rem] border border-zinc-200 bg-zinc-100 p-8">
                  <h3 className="mb-6 text-xl font-bold text-zinc-900">Good requests include</h3>
                  <ul className="space-y-4">
                    {[
                      'The exact workflow that feels broken or incomplete.',
                      'Why the change matters in your process.',
                      'Any specific chart or broker context involved.',
                    ].map((item) => (
                      <li key={item} className="group flex gap-4 rounded-2xl border border-zinc-200/50 bg-white p-4 shadow-sm transition-colors hover:border-pink-200">
                        <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pink-50 transition-colors group-hover:bg-pink-500">
                          <ChevronRight className="h-3 w-3 text-pink-500 group-hover:text-white" />
                        </div>
                        <span className="text-sm font-medium leading-relaxed text-zinc-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0a0b46] p-8 text-white">
                  <div className="absolute right-0 top-0 p-8 opacity-10">
                    <Sparkles className="h-24 w-24 rotate-12" />
                  </div>
                  <div className="relative z-10 mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-pink-500" />
                    <h3 className="text-xl font-bold text-white">Example</h3>
                  </div>
                  <p className="relative z-10 rounded-2xl border border-white/10 bg-white/5 p-6 text-sm italic leading-relaxed text-zinc-300">
                    "It takes me 5 clicks to export my trade logs to a CSV. It would be great if there was a one-click 'Quick Export' button on the main dashboard to save me time during my end-of-day review."
                  </p>
                </div>

                <div className="relative mt-auto flex flex-col overflow-hidden rounded-[2.5rem] border border-zinc-200 bg-white p-8 shadow-sm">
                  <div className="mb-6 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-pink-500" />
                    <h3 className="text-xl font-bold text-zinc-900">Live Roadmap</h3>
                  </div>

                  <div className="relative z-10 space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-2 shrink-0 flex-col items-center rounded-full bg-zinc-100 py-1">
                        <div className="h-2 w-2 rounded-full bg-pink-500" />
                      </div>
                      <div className="pt-0.5">
                        <p className="mb-1 text-xs font-black uppercase tracking-wider text-zinc-900">Reviewing</p>
                        <p className="text-sm leading-normal text-zinc-500">Suggestions enter our internal queue for founder review.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-2 shrink-0 flex-col items-center rounded-full bg-zinc-100 py-1">
                        <div className="h-2 w-2 rounded-full bg-zinc-300" />
                      </div>
                      <div className="pt-0.5">
                        <p className="mb-1 text-xs font-black uppercase tracking-wider text-zinc-400">Planning</p>
                        <p className="text-sm leading-normal text-zinc-500">High-impact ideas move to active technical scoping.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex h-8 w-2 shrink-0 flex-col items-center rounded-full bg-zinc-100 py-1">
                        <Zap className="h-2.5 w-2.5 text-zinc-300" />
                      </div>
                      <div className="pt-0.5">
                        <p className="mb-1 text-xs font-black uppercase tracking-wider text-zinc-400">Production</p>
                        <p className="text-sm leading-normal text-zinc-500">Verified features are rolled out in weekly updates.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}